import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { StayCompliantNavItem } from "../../../../common/service/server/Types";
import { RouteHelpers, Routes } from "../../../root/routes";
import { isRamsaySectionEnabled } from "../../../util/ramsay";

export type NavItem = {
  item: StayCompliantNavItem;
  label: string;
  route?: string | ((organizationId: OrganizationDto["id"]) => string);
  hidden?: boolean | ((org: OrganizationDto) => boolean);
  isDisabled?: boolean;
  itemAliases?: string[];
  children?: NavItem[];
  matchRouteOnPrefix?: boolean; // If true, will consider the nav item as active it the current page's route starts with the nav item's route
};

export const DIVIDER_LITERAL = "DIVIDER";
export type NavDivider = "DIVIDER";

// Uppercase section heading rendered between nav groups (e.g. "Compliance")
export type NavSection = { sectionLabel: string };
export const isNavSection = (
  item: NavItem | NavDivider | NavSection | null | undefined,
): item is NavSection => typeof item === "object" && item !== null && "sectionLabel" in item;

const KNOWLEDGE_CENTER_NAV_ITEM: NavItem = {
  item: "KNOWLEDGE_CENTER",
  label: "Knowledge Center",
  route: Routes.knowledgeCenter.root,
};

const REQUEST_INBOX_NAV_ITEM: NavItem = {
  item: "REQUEST_INBOX",
  label: "Request Inbox",
  route: Routes.requests.active,
  itemAliases: ["/requests/"],
};

// Shown only for organizations with at least one Ramsay feature flag on (see `isRamsaySectionEnabled`)
const RAMSAY_NAV_ITEM: NavItem = {
  item: "RAMSAY",
  label: "Ramsay",
  route: Routes.ramsay.root,
  hidden: (org) => !isRamsaySectionEnabled(org),
  children: [
    {
      item: "RAMSAY_SERVICE_ACCOUNT_INBOX",
      label: "Service Account Inbox",
      route: Routes.ramsay.serviceAccountInbox,
    },
    {
      item: "RAMSAY_SERVICE_ACCOUNT_SETUP",
      label: "Service Account Setup",
      route: Routes.ramsay.serviceAccountSetup,
    },
  ],
};

const HR_CENTER_NAV_ITEM: NavItem = {
  item: "HR_CENTER",
  label: "HR Center",
  route: Routes.hrCenter.root,
  children: [
    {
      item: "HR_CENTER_HR_DATA_MAP",
      label: "Data Map",
      route: Routes.hrCenter.hrDataMap.root,
      itemAliases: [Routes.hrCenter.hrVendorInventory],
      matchRouteOnPrefix: true,
    },
    {
      item: "HR_CENTER_DSAR_WORKFLOWS",
      label: "DSAR Workflows",
      route: Routes.hrCenter.hrDsarWorkflows,
    },
    {
      item: "HR_CENTER_HR_NOTICES",
      label: "Notices",
      route: Routes.hrCenter.hrNotices,
    },
  ],
};

export const NAV_ITEMS: (NavItem | NavDivider | NavSection)[] = [
  {
    item: "DASHBOARD",
    label: "Dashboard",
    route: Routes.dashboard.root,
  },
  REQUEST_INBOX_NAV_ITEM,
  { item: "TASK_LIST", label: "To-Do List", route: Routes.tasks.open },
  { sectionLabel: "Compliance" },
  {
    item: "WORKFLOWS",
    label: "DSAR Workflows",
    route: Routes.workflows.dashboard,
    children: [
      {
        item: "WORKFLOWS_API_INTEGRATIONS",
        label: "API Integrations",
        route: Routes.privacyCenter.integrations,
      },
      {
        item: "WORKFLOWS_DATA_STORAGE_SETTINGS",
        label: "Data Storage",
        route: Routes.workflows.dataStorageSettings,
      },
      {
        item: "WORKFLOWS_REQUESTS_WORKFLOWS",
        label: "Workflow Setup",
        route: Routes.organization.requestHandling.base,
        matchRouteOnPrefix: true,
      },
      {
        item: "WORKFLOWS_EMAIL_TEMPLATES",
        label: "Email Templates",
        route: Routes.workflows.emailTemplates,
      },
    ],
  },
  {
    item: "DATA_MAP_DASHBOARD",
    label: "Data Map",
    route: Routes.dataMap.dashboard,
    children: [
      {
        item: "BUSINESS_SURVEY",
        label: "Business Profile",
        route: Routes.organization.settings.businessSurvey,
      },
      {
        item: "VENDOR_INVENTORY",
        label: "Vendor List",
        route: Routes.dataMap.vendorInventory,
      },
      {
        item: "DATA_FLOWS",
        label: "Data Flows",
        route: Routes.dataMap.dataFlows.dashboard,
        itemAliases: [
          Routes.dataMap.dataFlows.collection,
          Routes.dataMap.dataFlows.disclosures,
          Routes.dataMap.dataFlows.purposes,
          Routes.dataMap.dataFlows.sources,
        ],
        matchRouteOnPrefix: true,
      },
      {
        item: "VENDOR_DOCUMENTATION",
        label: "Vendor Documents",
        route: Routes.dataMap.vendorDocumentation,
      },
      {
        item: "DATA_RETENTION",
        label: "Data Retention",
        route: Routes.dataMap.dataRetention,
      },
    ],
  },
  {
    item: "PRIVACY_CENTER",
    label: "Privacy Center",
    route: Routes.privacyCenter.root,
    children: [
      {
        item: "PRIVACY_CENTER_POLICIES_AND_NOTICES",
        label: "Policies & Notices",
        route: Routes.privacyCenter.privacyPolicy,
        itemAliases: [Routes.privacyCenter.usStates, Routes.privacyCenter.theme],
      },
      {
        item: "PRIVACY_CENTER_DSAR_FORMS",
        label: "Request Forms",
        route: Routes.privacyCenter.requestForms,
      },
      {
        item: "PRIVACY_CENTER_DEVELOPER_INSTRUCTIONS",
        label: "Developer Instructions",
        route: Routes.privacyCenter.publish,
      },
      {
        item: "PRIVACY_CENTER_WEBSITE_CONSENT",
        label: "Website Consent",
        route: Routes.privacyCenter.consentBanner,
      },
    ],
  },
  RAMSAY_NAV_ITEM,
  HR_CENTER_NAV_ITEM,
  { sectionLabel: "Resources" },
  { item: "DOCUMENTS", label: "Privacy Docs", route: Routes.documents.root },
  KNOWLEDGE_CENTER_NAV_ITEM,
  {
    item: "EVENT_PULSE",
    label: "SignalOps",
    route: (organizationId) => RouteHelpers.organization.eventPulse(organizationId),
    hidden: (org) => org.featureEventPulse !== true,
  },
];

export const NAV_ITEMS_GENERAL_ONLY = NAV_ITEMS.filter(
  (item) =>
    item == DIVIDER_LITERAL ||
    isNavSection(item) ||
    ((item as NavItem).item !== "DOCUMENTS" &&
      // See POL-6912 for context to not allow General users to access HR Center
      (item as NavItem).item !== "HR_CENTER"),
);

export const NAV_ITEMS_HR_ONLY: (NavItem | NavDivider)[] = [
  REQUEST_INBOX_NAV_ITEM,
  DIVIDER_LITERAL,
  KNOWLEDGE_CENTER_NAV_ITEM,
  HR_CENTER_NAV_ITEM,
];
